/**
 * Connect Real-Time Signals Panel
 * 
 * Displays after campaigns are confirmed to Bucket List.
 * Allows users to generate, select, and save real-time signals
 * that can trigger campaign alerts.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Zap,
  Radio,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  AlertCircle,
  Search,
  Calendar,
  Building2,
  TrendingUp,
  Globe,
  Users,
  Newspaper,
  HelpCircle,
  Sparkles,
  Save,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  suggestSignals,
  saveSignals,
  getSignalsForPlan,
  SIGNAL_CATEGORIES
} from '../api/signalsApi';

// Category icons
const CATEGORY_ICONS = {
  regulatory: AlertCircle,
  seasonal: Calendar,
  competitor: Building2,
  market: TrendingUp,
  platform: Globe,
  customer_intent: Users,
  company_events: Building2,
  content_trends: Newspaper,
  other: HelpCircle
};

// Generation step labels
const GENERATION_STEPS = [
  { key: 'draft', label: 'Drafting signals...' },
  { key: 'expand', label: 'Expanding & personalizing...' },
  { key: 'qa', label: 'QA & finalizing...' }
];

export default function SignalsPanel({
  assetId,
  confirmedPlanId,
  confirmedCampaignIds = [],
  step0Data,
  step1Data,
  step2Data,
  assetBundle,
  onSignalsSaved
}) {
  // State
  const [signals, setSignals] = useState([]);
  const [selectedSignalIds, setSelectedSignalIds] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedSignals, setHasSavedSignals] = useState(false);
  const [expandedSignals, setExpandedSignals] = useState(new Set());
  const [metadata, setMetadata] = useState(null);

  // Load existing saved signals on mount
  useEffect(() => {
    if (confirmedPlanId) {
      loadSavedSignals();
    }
  }, [confirmedPlanId]);

  const loadSavedSignals = async () => {
    try {
      const result = await getSignalsForPlan(confirmedPlanId);
      if (result.signals && result.signals.length > 0) {
        setSignals(result.signals);
        setSelectedSignalIds(new Set(result.signals.map(s => s.signal_id)));
        setHasSavedSignals(true);
      }
    } catch (err) {
      console.log('No saved signals found:', err.message);
    }
  };

  // Group signals by category
  const signalsByCategory = useMemo(() => {
    const grouped = {};
    for (const signal of signals) {
      const cat = signal.category || 'other';
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(signal);
    }
    // Sort categories by signal count (descending)
    return Object.entries(grouped)
      .sort((a, b) => b[1].length - a[1].length)
      .reduce((acc, [key, val]) => {
        acc[key] = val;
        return acc;
      }, {});
  }, [signals]);

  // Generate signals
  const handleGenerateSignals = async () => {
    if (!assetId || !confirmedPlanId) {
      toast.error('Missing required data to generate signals');
      return;
    }

    setIsGenerating(true);
    setGenerationStep(0);
    setSignals([]);
    setSelectedSignalIds(new Set());

    // Simulate step progression (actual backend runs all 3 steps)
    const stepInterval = setInterval(() => {
      setGenerationStep(prev => Math.min(prev + 1, 2));
    }, 3000);

    try {
      const result = await suggestSignals({
        assetId,
        confirmedPlanId,
        step0GoalOffer: step0Data || {},
        step1Persona: step1Data?.selected_personas?.[0] || step1Data || {},
        step2Segments: step2Data?.selected_segments || step2Data || [],
        assetBundle: assetBundle || {}
      });

      clearInterval(stepInterval);
      setGenerationStep(2);

      if (result.success && result.signals) {
        setSignals(result.signals);
        setMetadata(result.metadata);
        toast.success(`Generated ${result.signals.length} signals`);
      } else {
        toast.error('Failed to generate signals');
      }
    } catch (err) {
      clearInterval(stepInterval);
      console.error('Error generating signals:', err);
      toast.error(err.message || 'Failed to generate signals');
    } finally {
      setIsGenerating(false);
    }
  };

  // Toggle signal selection
  const toggleSignal = (signalId) => {
    setSelectedSignalIds(prev => {
      const next = new Set(prev);
      if (next.has(signalId)) {
        next.delete(signalId);
      } else {
        next.add(signalId);
      }
      return next;
    });
  };

  // Toggle all signals in a category
  const toggleCategory = (category) => {
    const categorySignals = signalsByCategory[category] || [];
    const categoryIds = categorySignals.map(s => s.signal_id);
    const allSelected = categoryIds.every(id => selectedSignalIds.has(id));

    setSelectedSignalIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        categoryIds.forEach(id => next.delete(id));
      } else {
        categoryIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Toggle signal details expansion
  const toggleExpand = (signalId) => {
    setExpandedSignals(prev => {
      const next = new Set(prev);
      if (next.has(signalId)) {
        next.delete(signalId);
      } else {
        next.add(signalId);
      }
      return next;
    });
  };

  // Save selected signals
  const handleSaveSignals = async () => {
    if (selectedSignalIds.size === 0) {
      toast.error('Please select at least one signal');
      return;
    }

    setIsSaving(true);

    try {
      const selectedSignals = signals
        .filter(s => selectedSignalIds.has(s.signal_id))
        .map(s => ({ ...s, asset_id: assetId }));

      const result = await saveSignals({
        confirmedPlanId,
        confirmedCampaignIds,
        selectedSignals
      });

      if (result.success) {
        setHasSavedSignals(true);
        toast.success(`Saved ${result.saved_count} signals to Bucket List plan`);
        if (onSignalsSaved) {
          onSignalsSaved(selectedSignals);
        }
      } else {
        toast.error('Failed to save signals');
      }
    } catch (err) {
      console.error('Error saving signals:', err);
      toast.error(err.message || 'Failed to save signals');
    } finally {
      setIsSaving(false);
    }
  };

  // Render confidence badge
  const renderConfidenceBadge = (confidence) => {
    const value = typeof confidence === 'number' ? confidence : 0;
    let color = 'bg-slate-100 text-slate-600';
    if (value >= 0.8) color = 'bg-emerald-100 text-emerald-700';
    else if (value >= 0.6) color = 'bg-blue-100 text-blue-700';
    else if (value >= 0.4) color = 'bg-amber-100 text-amber-700';

    return (
      <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>
        {Math.round(value * 100)}%
      </span>
    );
  };

  // Render a single signal row
  const renderSignalRow = (signal) => {
    const isSelected = selectedSignalIds.has(signal.signal_id);
    const isExpanded = expandedSignals.has(signal.signal_id);
    const CategoryIcon = CATEGORY_ICONS[signal.category] || HelpCircle;
    const howToTrack = signal.how_to_track || {};

    return (
      <div
        key={signal.signal_id}
        className={`border rounded-lg transition-all ${
          isSelected ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200 bg-white'
        }`}
      >
        {/* Main row */}
        <div className="p-3 flex items-start gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleSignal(signal.signal_id)}
            className="mt-1"
            data-testid={`signal-checkbox-${signal.signal_id}`}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-slate-900 text-sm">{signal.title}</span>
              {renderConfidenceBadge(signal.confidence)}
            </div>
            
            <p className="text-sm text-slate-500 line-clamp-2">
              {signal.trigger_definition}
            </p>

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className={`text-xs ${SIGNAL_CATEGORIES[signal.category]?.color || ''}`}>
                <CategoryIcon className="w-3 h-3 mr-1" />
                {SIGNAL_CATEGORIES[signal.category]?.label || signal.category}
              </Badge>
              
              {signal.who_it_applies_to?.segment_ids?.length > 0 && (
                <Badge variant="outline" className="text-xs bg-slate-50">
                  <Users className="w-3 h-3 mr-1" />
                  {signal.who_it_applies_to.segment_ids.length} segment(s)
                </Badge>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleExpand(signal.signal_id)}
            className="shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="px-3 pb-3 pt-0 border-t border-slate-100 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
              {/* How to track */}
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  How to Track
                </p>
                <div className="space-y-1">
                  {howToTrack.query_terms?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Search className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <span className="text-slate-600">
                        {howToTrack.query_terms.join(', ')}
                      </span>
                    </div>
                  )}
                  {howToTrack.entities?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <span className="text-slate-600">
                        {howToTrack.entities.join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>Source: {howToTrack.source_type || 'web_search'}</span>
                    <span>Frequency: {howToTrack.frequency || 'weekly'}</span>
                    {howToTrack.geo && howToTrack.geo !== 'any' && (
                      <span>Geo: {howToTrack.geo}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Recommended action */}
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Recommended Action
                </p>
                <p className="text-slate-600">{signal.recommended_action}</p>
              </div>
            </div>

            {/* Why relevant */}
            {signal.why_relevant && (
              <div className="mt-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Why Relevant
                </p>
                <p className="text-sm text-slate-600">{signal.why_relevant}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-slate-200" data-testid="signals-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <Radio className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Connect Real-Time Signals</CardTitle>
            <CardDescription>
              Track real-world triggers for your personas and get alerted when it's time to run a campaign.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Intro bullets (shown before generation) */}
        {signals.length === 0 && !isGenerating && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2 text-sm text-slate-600">
              <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span>We suggest triggers based on your asset + persona + segments.</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-slate-600">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span>Pick the signals you care about — we'll alert you when they spike.</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-slate-600">
              <RefreshCw className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <span>Signals are editable later.</span>
            </div>
          </div>
        )}

        {/* Generate button - Disabled for now */}
        {signals.length === 0 && !isGenerating && (
          <Button
            disabled
            className="w-full bg-amber-500/50 text-white cursor-not-allowed"
            data-testid="suggest-signals-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Suggest Signals (Coming Soon)
          </Button>
        )}

        {/* Generation progress */}
        {isGenerating && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
              <span className="text-sm font-medium text-slate-700">Generating signals...</span>
            </div>
            <div className="space-y-2">
              {GENERATION_STEPS.map((step, idx) => (
                <div
                  key={step.key}
                  className={`flex items-center gap-2 text-sm ${
                    idx < generationStep
                      ? 'text-emerald-600'
                      : idx === generationStep
                      ? 'text-amber-600'
                      : 'text-slate-400'
                  }`}
                >
                  {idx < generationStep ? (
                    <Check className="w-4 h-4" />
                  ) : idx === generationStep ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-slate-300" />
                  )}
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signals list by category */}
        {signals.length > 0 && !isGenerating && (
          <>
            {/* Metadata summary */}
            {metadata && (
              <div className="flex items-center gap-4 text-xs text-slate-500 pb-2 border-b border-slate-100">
                <span>{metadata.final_count} signals</span>
                <span>{Object.keys(signalsByCategory).length} categories</span>
                {metadata.removed_count > 0 && (
                  <span>{metadata.removed_count} filtered out</span>
                )}
              </div>
            )}

            {/* Category accordions */}
            <Accordion type="multiple" defaultValue={Object.keys(signalsByCategory)} className="space-y-2">
              {Object.entries(signalsByCategory).map(([category, categorySignals]) => {
                const CategoryIcon = CATEGORY_ICONS[category] || HelpCircle;
                const categoryInfo = SIGNAL_CATEGORIES[category] || {};
                const categoryIds = categorySignals.map(s => s.signal_id);
                const selectedCount = categoryIds.filter(id => selectedSignalIds.has(id)).length;
                const allSelected = selectedCount === categoryIds.length;

                return (
                  <AccordionItem
                    key={category}
                    value={category}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Custom header layout to avoid nesting Checkbox inside AccordionTrigger button */}
                    <div className="flex items-center px-4 py-3 hover:bg-slate-50">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => toggleCategory(category)}
                        className="mr-3"
                        data-testid={`category-checkbox-${category}`}
                      />
                      <AccordionTrigger className="flex-1 p-0 hover:no-underline">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${categoryInfo.color || 'bg-slate-100'}`}>
                            <CategoryIcon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-slate-900">
                            {categoryInfo.label || category}
                          </span>
                          <Badge variant="secondary" className="ml-auto mr-2">
                            {selectedCount}/{categorySignals.length}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-2 pt-2">
                        {categorySignals.map(renderSignalRow)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* Save button */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateSignals}
                disabled={isGenerating}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>

              <Button
                onClick={handleSaveSignals}
                disabled={isSaving || selectedSignalIds.size === 0}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-signals-btn"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Selected ({selectedSignalIds.size})
              </Button>
            </div>

            {/* Saved indicator */}
            {hasSavedSignals && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
                <Check className="w-4 h-4" />
                <span>Signals saved to Bucket List plan</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
